import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplumContext } from "@medplum/react-hooks";
import { captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";

import { FullUser, Role, UserAccess } from "@/types/users";
import { FHIR_EXTENSIONS } from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";
import { medplumClient } from "@/lib/medplum";
import { posthog } from "@/lib/posthog";
import {
  buildUserAccess,
  deriveRole,
  NO_ACCESS,
  parseMemberRole,
} from "@/lib/user-access";
import { getExtensionValue } from "@/lib/utils";
import { getReferenceId, getTelecomValue } from "@/lib/utils/helpers";
import { fetchAccessPolicyByRole, fetchMember } from "@/apis/medplum";

import { Loading } from "@/components/module/Loading";

type UserProviderState = {
  login: () => void;
  logout: () => void;
  user: FullUser | null;
  role: Role | null;
  isSuperAdmin: boolean;
  access: UserAccess;
  error: Error | null;
  isAuthenticated: boolean;
  userLoading: boolean;
  accessLoading: boolean;
  getAccessToken: () => Promise<string | null>;
};

const initialState: UserProviderState = {
  login: () => null,
  logout: () => null,
  user: null,
  role: null,
  isSuperAdmin: false,
  access: NO_ACCESS,
  error: null,
  isAuthenticated: false,
  userLoading: true,
  accessLoading: true,
  getAccessToken: () => Promise.resolve(""),
};

const UserProviderContext = createContext<UserProviderState>(initialState);

const buildFullUser = (practitioner: Practitioner): FullUser => {
  const humanName = practitioner.name?.[0];
  const given = humanName?.given?.join(" ") ?? "";
  const family = humanName?.family ?? "";
  const fullName = [given, family].filter(Boolean).join(" ");
  const email = getTelecomValue(practitioner.telecom, "email") ?? "";
  const activeLogin = medplumClient.getActiveLogin();
  const projectId = getReferenceId(activeLogin?.project?.reference) ?? "";

  return {
    id: practitioner.id ?? "",
    fullName,
    email,
    projectId,
    memberships: [],
  };
};

type Props = {
  children: React.ReactNode;
};

export const UserProvider: React.FC<Props> = ({ children }) => {
  const { loading, profile } = useMedplumContext();
  const queryClient = useQueryClient();

  const isAuthenticated = medplumClient.isAuthenticated();
  const isSuperAdmin = medplumClient.isSuperAdmin();

  /**
   * "App-valid" means Medplum finished loading AND the profile is a
   * Practitioner — the only account type this app supports. A valid Medplum
   * session with a Patient or Admin profile is Medplum-valid but not
   * app-valid, and must never suppress the full-screen loader permanently.
   */
  const isAppValid =
    !loading && isAuthenticated && profile?.resourceType === "Practitioner";

  const isUnsupportedProfileType =
    !loading && isAuthenticated && Boolean(profile) && !isAppValid;
  const [user, setUser] = useState<FullUser | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [access, setAccess] = useState<UserAccess>(NO_ACCESS);
  const [accessLoading, setAccessLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);

  const login = useCallback(() => {
    window.location.assign(ROUTES.LOGIN);
  }, []);

  const logout = useCallback(() => {
    posthog.reset();

    /**
     * Logging out is a client-side navigation, so the tab — and the React
     * Query cache with it — outlives the session. Drop every cached query
     * before signing out, or the next login in this tab reads this one's data.
     */
    queryClient.clear();

    return medplumClient.signOut().catch(captureException);
  }, [queryClient]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      await medplumClient.refreshIfExpired();
    } catch (refreshError) {
      captureException(refreshError);
    }

    const token = medplumClient.getAccessToken();

    if (!token) {
      login();

      return null;
    }

    return token;
  }, [login]);

  useEffect(() => {
    if (!isAppValid) {
      return;
    }

    const run = async () => {
      const fullUser = buildFullUser(profile as Practitioner);
      const membership = medplumClient.getProjectMembership();

      setUser(fullUser);

      const staleProperties: string[] = [];
      const identifyProperties: Record<string, string | boolean> = {
        isSuperAdmin,
      };

      if (fullUser.projectId) {
        identifyProperties.projectId = fullUser.projectId;
      } else {
        staleProperties.push("projectId");
      }

      if (membership?.id) {
        identifyProperties.membershipId = membership.id;
      } else {
        staleProperties.push("membershipId");
      }

      posthog.identify(fullUser.id, identifyProperties);
      posthog.unset(staleProperties);

      if (membership) {
        const practitionerId = (profile as Practitioner).id ?? "";
        const member = isSuperAdmin
          ? undefined
          : await fetchMember(practitionerId);
        const memberRole = parseMemberRole(
          getExtensionValue<string>(member, FHIR_EXTENSIONS.MEMBER_ROLE)
        );
        const accessPolicy = memberRole
          ? await fetchAccessPolicyByRole(memberRole)
          : undefined;

        setRole(deriveRole(isSuperAdmin, membership, memberRole));
        setAccess(
          buildUserAccess(isSuperAdmin, membership, accessPolicy, memberRole)
        );
      }

      setAccessLoading(false);
    };

    run().catch((reason: unknown) => {
      setError(reason instanceof Error ? reason : new Error(String(reason)));
      setAccessLoading(false);
    });
  }, [isAppValid, profile, isSuperAdmin]);

  const value = useMemo(
    () => ({
      user,
      role,
      isSuperAdmin,
      access,
      error,
      login,
      logout,
      isAuthenticated,
      userLoading: loading,
      accessLoading,
      getAccessToken,
    }),
    [
      user,
      role,
      isSuperAdmin,
      access,
      error,
      isAuthenticated,
      loading,
      accessLoading,
      login,
      logout,
      getAccessToken,
    ]
  );

  const hasActiveLogin = Boolean(medplumClient.getActiveLogin());

  /**
   * Medplum finished loading with a non-Practitioner profile. Sign out first
   * (via the reset-aware logout callback, so PostHog's identity is cleared
   * too) then hard-navigate to login — a plain <Navigate> would trigger a
   * redirect loop because the guest route sees an active session and
   * bounces straight back to /home.
   */
  useEffect(() => {
    if (!isUnsupportedProfileType) {
      return;
    }

    void logout().finally(() => {
      window.location.assign(ROUTES.LOGIN);
    });
  }, [isUnsupportedProfileType, logout]);

  useEffect(() => {
    if (error) {
      captureException(error);
    }
  }, [error]);

  /**
   * Latch: once we reach a fully app-valid state, suppress the full-screen
   * loader for any subsequent loading events (e.g. project switch). Project
   * switches call setActiveLogin, which clears the cached profile and flips
   * Medplum loading back to true — that must be invisible to the user.
   */
  if (isAppValid && !hasBootstrapped) {
    setHasBootstrapped(true);
  }

  /**
   * Show the full-screen loader only during initial bootstrap, never
   * mid-session. The guard (isAuthenticated || hasActiveLogin) prevents
   * showing it for first-time visitors who have no stored session — they go
   * straight to the Navigate branch below.
   */
  if (loading && !hasBootstrapped && (isAuthenticated || hasActiveLogin)) {
    return <Loading />;
  }

  /**
   * Genuinely no session: redirect to login. hasActiveLogin guards against
   * premature redirects during the SDK's grace-period window — isAuthenticated()
   * returns false up to 5 minutes before actual token expiry, but the stored
   * login is still there and the next FHIR request will trigger a silent refresh.
   */
  if (!isAuthenticated && !hasActiveLogin) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <UserProviderContext.Provider value={value}>
      {children}
    </UserProviderContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserProviderContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};
